import { ApiError } from './response';
import { authFetch } from '../utils/authFetch';

export interface BundleMetaCache {
  backend: string;
  hit: boolean;
  ttl_seconds: number;
}

export interface BundleMeta {
  schema_version: string;
  generated_at: string;
  request_id: string | null;
  partial: boolean;
  warnings: string[];
  cache: BundleMetaCache;
}

export interface BundleEnvelope<TData> {
  meta: BundleMeta;
  data: TData;
}

function isObject(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

export function isBundleEnvelope<TData>(value: unknown): value is BundleEnvelope<TData> {
  if (!isObject(value)) return false;
  if (!('meta' in value) || !('data' in value)) return false;
  return isObject((value as { meta?: unknown }).meta);
}

export async function fetchBundle<TData>(
  path: string,
  init?: RequestInit & { autoLogout?: boolean; skipDedupe?: boolean },
): Promise<BundleEnvelope<TData>> {
  const response = await authFetch(path, init);
  if (!response.ok) {
    let detail = `HTTP ${response.status}`;
    try {
      const body = await response.json();
      if (isObject(body) && typeof body.detail === 'string' && body.detail.trim()) {
        detail = body.detail.trim();
      }
    } catch {
      // Ignore parse errors and fallback to status.
    }
    throw new ApiError(detail, {kind:'http',status:response.status});
  }

  const payload = (await response.json()) as unknown;
  if (!isBundleEnvelope<TData>(payload)) {
    throw new Error('Invalid bundle response shape');
  }
  return payload;
}

export async function fetchBundleData<TData>(
  path: string,
  init?: RequestInit & { autoLogout?: boolean; skipDedupe?: boolean },
): Promise<TData> {
  const payload = await fetchBundle<TData>(path, init);
  return payload.data;
}

